import React, { useCallback, useRef } from 'react';
import {
  Button, ButtonColor, ButtonSize, ButtonVariant, SpacingName, TypographyName,
} from '@doveit/lego';
import { NextPage } from 'next';
import Hero from '../client/templates/common/hero/Hero';
import InfoSection, { InfoSectionProps } from '../client/templates/common/info-section/InfoSection';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import SectionDivider from '../client/components/section-divider/SectionDivider';
import CitiesSection from '../client/templates/common/cities-section/CitiesSection';
import { requireFromStaticAsset, requireResourcesFromStaticAssets } from '../client/utils/assetsUtils';
import provincesToLinkGroups from '../client/utils/buy-property/provincesToLinkGroups';
import {
  goToPage, buildSerpURLObject, buildListingURLObject, URLObject,
} from '../client/utils/links';
import LinkGroupsSection, { LinkGroupsSectionProps } from '../client/templates/common/link-groups-section/LinkGroupsSection';
import { getAggregationsByProvinceAndLocality, getAll } from '../services/properties/properties';
import Stripe, { StripeAlignemnt } from '../client/components/stripe/Stripe';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { getMinSoldAtDate } from '../client/utils/search/filters';
import {
  City, CTA, Feature, HeroSection, TextAlign,
} from '../client/types';
import PropertyFilterForm from '../client/components/property-filter-form/PropertyFilterForm';
import { mapBuildSerpUrlObject } from '../client/utils/serp/mapBuildSerpUrlObject';
import { ReviewTarget } from '../domain/graphcms';
import PageSection from '../client/components/page-section/PageSection';
import ListReviews from '../client/containers/list-reviews/ListReviews';
import TrustpilotMicroReviewCount from '../client/components/trustpilot/trustpilot-microreview-count/TrustpilotMicroReviewCount';
import { Status } from '../domain/status';
import { SortOrder } from '../domain/pagination';
import { getLogger } from '../logger/isomorphic';
import { PropertySearchResult } from '../services/dtos/property';
import PropertySearchResultCardList from '../client/components/property-search-result-card-list/PropertySearchResultCardList';
import FeaturedBox from '../client/components/featured-box/FeaturedBox';
import { getHighlightedCities } from '../services/cities/cities';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';

export interface BuyPropertyProps {
  SEO: PageHeadProps['SEO'],
  cities: City[],
  hero: HeroSection,
  newBuildingsSection: Feature,
  citiesSection: {
    title: string,
    cta: {
      label: string,
    },
  },
  infoSection: InfoSectionProps,
  reviewsSection: {
    title: string,
    description: string,
  },
  localitiesSection: Pick<LinkGroupsSectionProps, 'title' | 'description'>,
  linkGroups: LinkGroupsSectionProps['linkGroups'],
  latestProperties: {
    title: string,
    items: PropertySearchResult[],
    cta: CTA,
  },
  buyerConsultancy: {
    caption: string,
    title: string,
    description: string,
    cta: CTA & { urlObject: URLObject },
  },
}

const logger = getLogger();

export const BuyProperty: NextPage<BuyPropertyProps> = ({
  SEO,
  cities,
  hero,
  citiesSection,
  infoSection,
  reviewsSection,
  localitiesSection,
  newBuildingsSection,
  linkGroups,
  latestProperties,
  buyerConsultancy,
}) => {
  const serpURLObject = buildSerpURLObject();
  const formRef = useRef();

  const internalGoToPage = useCallback((urlObject) => () => {
    goToPage(urlObject);
  }, []);

  const goToSerp = useCallback((values) => {
    goToPage(buildSerpURLObject(values ? mapBuildSerpUrlObject(values) : undefined));
  }, []);

  return (
    <AppLayout activePage="buy-property">
      <PageHead SEO={SEO} />

      <Hero
        title={hero.title}
        backgroundImage={requireResourcesFromStaticAssets(hero.backgroundImage)}
        description={hero.description}
        secondarySlotTitle={hero.formTitle}
        secondarySlot={(
          <div ref={formRef}>
            <PropertyFilterForm
              onSubmit={goToSerp}
            />
          </div>
        )}
      />

      <CitiesSection
        title={citiesSection.title}
        cities={cities.map((city) => {
          const urlObject = buildSerpURLObject({ locality: city.locality });

          return {
            ...city,
            anchorTitle: `Case a ${city.title}`,
            urlObject,
            onClick: internalGoToPage(urlObject),
          };
        })}
        cta={(
          <Button
            {...citiesSection.cta}
            color={ButtonColor.SECONDARY}
            href={buildSerpURLObject().as}
            onClick={goToSerp}
          />
        )}
      />

      <SectionDivider />

      <PageSection>
        <PageSection.Wrapper>
          <Stripe
            title={newBuildingsSection.title}
            description={newBuildingsSection.description}
            alignContent={StripeAlignemnt.CENTER}
            picture={{
              src: requireFromStaticAsset(newBuildingsSection.picture),
              alt: `Icona ${newBuildingsSection.title}`,
            }}
          >
            <Button
              withoutSpacing
              label={newBuildingsSection.cta.label}
              color={ButtonColor.PRIMARY}
              variant={ButtonVariant.LIGHT}
              size={ButtonSize.SMALL}
              href={newBuildingsSection.cta.urlObject.as}
              onClick={internalGoToPage(newBuildingsSection.cta.urlObject)}
            />
          </Stripe>
        </PageSection.Wrapper>
      </PageSection>

      <SectionDivider />

      <InfoSection
        title={infoSection.title}
        reasons={infoSection.reasons}
      />

      <PageSection backgroundColor="neutrals.lighter">
        <PageSection.Wrapper>
          <PageSection.Title textAlign={TextAlign.CENTER}>
            {reviewsSection.title}
          </PageSection.Title>
          <PageSection.Description
            text={reviewsSection.description}
            textAlign={TextAlign.CENTER}
          />
        </PageSection.Wrapper>
        <PageSection.Wrapper withoutPad>
          <PageSection.Block>
            <ListReviews
              count={5}
              target={[ReviewTarget.COMPRATORE]}
              showLink
            />
          </PageSection.Block>
          <PageSection.Block>
            <TrustpilotMicroReviewCount />
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      {latestProperties?.items.length > 0 && (
        <PageSection
          padding={[SpacingName.BIGGER, 0, 0]}
          data-ref="latest-properties-section"
        >
          <PageSection.Wrapper>
            <PageSection.Title
              size={TypographyName.H3}
              content={latestProperties.title}
            />
          </PageSection.Wrapper>
          <PageSection.Wrapper withoutPad>
            <PageSection.Block>
              <PropertySearchResultCardList items={latestProperties.items.map((searchResult) => ({
                searchResult,
                urlObject: buildListingURLObject(searchResult),
                onClick: internalGoToPage(buildListingURLObject(searchResult)),
                overriddenAlt: `Vendita casa di ${searchResult.highlights.find((highlight) => highlight.label === 'superficie').value}m² in ${searchResult.route}, ${searchResult.locality} a ${searchResult.price}`,
              }))}
              />
            </PageSection.Block>
          </PageSection.Wrapper>
          <PageSection.Wrapper>
            <PageSection.Actions textAlign={TextAlign.CENTER}>
              <Button
                label={latestProperties.cta.label}
                dataRef={latestProperties.cta.dataRef}
                dataTracking={latestProperties.cta.dataTracking}
                href={serpURLObject.as}
                onClick={internalGoToPage(serpURLObject)}
              />
            </PageSection.Actions>
          </PageSection.Wrapper>
        </PageSection>
      )}
      <PageSection>
        <PageSection.Wrapper>
          <PageSection.Block>
            <FeaturedBox
              caption={buyerConsultancy.caption}
              title={buyerConsultancy.title}
              description={buyerConsultancy.description}
              cta={{
                ...buyerConsultancy.cta,
                href: buyerConsultancy.cta.urlObject.as,
                onClick: internalGoToPage(buyerConsultancy.cta.urlObject),
              }}
            />
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>
      <LinkGroupsSection
        title={localitiesSection.title}
        description={localitiesSection.description}
        linkGroups={linkGroups}
        onLinkClick={goToPage}
        onTitleClick={goToPage}
      />
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<BuyPropertyProps>(async () => {
  const [common, data, properties, cities] = await Promise.all([
    importDynamicData('common.yml'),
    importDynamicData('pages/buy-property.yml'),
    getAll({
      status: Status.LIVE,
    }, {
      size: 4,
      sort: {
        published_at: SortOrder.DESC,
      },
    })
      .catch((err) => {
        logger.warn({ err }, 'Error while retrieving properties');
        return {
          content: [],
        };
      }),
    getHighlightedCities(),
  ]);

  let linkGroups = [];

  try {
    const aggregations = await getAggregationsByProvinceAndLocality(getMinSoldAtDate().toISOString());
    linkGroups = provincesToLinkGroups(aggregations.province);
  } catch (err) {
    logger.error({ err });
  }

  return {
    props: {
      ...common,
      ...data,
      cities,
      latestProperties: {
        ...data.latestProperties,
        items: properties.content,
      },
      linkGroups,
    },
  };
});

export default withError(BuyProperty);
